import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Pill, TableSkeleton } from "../components/Pill";
import {
  REVIEW_SEVERITY_UI,
  REVIEW_SOURCE_UI,
  REVIEW_STATUS_UI,
} from "../lib/enumUI";
import { reviewQueueQueryOptions } from "./queries";
import type {
  ReviewItem,
  ReviewItemSeverity,
  ReviewItemSource,
  ReviewItemStatus,
} from "./types";

export function ReviewList() {
  const [statusFilter, setStatusFilter] = useState<ReviewItemStatus | "">("open");
  const [sourceFilter, setSourceFilter] = useState<ReviewItemSource | "">("");
  const [severityFilter, setSeverityFilter] = useState<ReviewItemSeverity | "">("");

  const filters: import("./types").ReviewQueueFilters = {};
  if (statusFilter) filters.status = statusFilter;
  if (sourceFilter) filters.source = sourceFilter;
  if (severityFilter) filters.severity = severityFilter;

  const { data, isLoading, error } = useQuery(reviewQueueQueryOptions(filters));

  return (
    <div className="page">
      <h1>Review queue</h1>
      <p className="subtitle">
        Cross-cutting view of every project-document item that needs human
        attention. Per-story queues remain available on their own pages.
      </p>

      <div className="filters">
        <label>
          Status
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ReviewItemStatus | "")}
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="suppressed">Suppressed</option>
          </select>
        </label>
        <label>
          Source
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value as ReviewItemSource | "")}
          >
            <option value="">All</option>
            <option value="ambiguous_payment_link">Ambiguous payment link</option>
            <option value="classification_review">Classification review</option>
            <option value="extraction_review">Extraction review</option>
            <option value="wrong_type_rejection">Wrong-type rejection</option>
            <option value="cross_check_mismatch">Cross-check mismatch</option>
          </select>
        </label>
        <label>
          Severity
          <select
            value={severityFilter}
            onChange={(e) =>
              setSeverityFilter(e.target.value as ReviewItemSeverity | "")
            }
          >
            <option value="">All</option>
            <option value="material_weakness">Material weakness</option>
            <option value="deficiency">Deficiency</option>
            <option value="informational">Informational</option>
          </select>
        </label>
      </div>

      {isLoading ? <TableSkeleton /> : null}

      {error ? (
        <p className="alert" role="alert">
          Failed to load review queue:{" "}
          {error instanceof Error ? error.message : String(error)}
        </p>
      ) : null}

      {!isLoading && !error ? <ReviewTable items={data?.review_items ?? []} /> : null}
    </div>
  );
}

function ReviewTable({ items }: { items: ReviewItem[] }) {
  if (items.length === 0) {
    return (
      <p className="empty-state">
        No review-required items match the current filters.
      </p>
    );
  }
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Opened</th>
          <th>Source</th>
          <th>Severity</th>
          <th>Status</th>
          <th>Summary</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <td>{new Date(item.opened_at).toLocaleString()}</td>
            <td>{REVIEW_SOURCE_UI[item.source].label}</td>
            <td>
              <Pill tone={REVIEW_SEVERITY_UI[item.severity].tone}>
                {REVIEW_SEVERITY_UI[item.severity].label}
              </Pill>
            </td>
            <td>
              <Pill tone={REVIEW_STATUS_UI[item.status].tone}>
                {REVIEW_STATUS_UI[item.status].label}
              </Pill>
            </td>
            <td>{item.summary}</td>
            <td>
              <a href={item.deep_link_path}>Open</a>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
